/**
 * Intervall-lathund: cykel (% av FTP) och löpning (tävlingstakt → min/km).
 * Löpning översätter inte FTP-procent rakt av — farter härleds från ett aktuellt
 * tävlingsresultat via Riegels formel.
 */

export type IntervalSport = "cycling" | "running";

export type TrainingGoal =
  | "anaerobic"
  | "vo2max"
  | "threshold"
  | "sweet_spot"
  | "economy";

export type ExperienceLevel = "beginner" | "intermediate" | "advanced";

export type RaceDistanceId = "5k" | "10k" | "half" | "marathon";

export type RunFocus = "5_10k" | "half" | "marathon" | "general";

/** Intensitetsnyckel för löppass — mappas till personlig farttabell. */
export type RunningPaceKey =
  | "economy"
  | "three_to_five_k"
  | "five_k"
  | "threshold"
  | "threshold_easy";

type TemplateBase = {
  id: string;
  goals: TrainingGoal[];
  name: string;
  durationLabel: string;
  repsMin: number;
  repsMax: number;
  restLabel: string;
  structureHint?: string;
  primaryEffect: string;
  tips: string[];
};

export type CyclingTemplate = TemplateBase & {
  sport: "cycling";
  intensityPctMin: number;
  intensityPctMax: number;
};

export type RunningTemplate = TemplateBase & {
  sport: "running";
  paceKey: RunningPaceKey;
  intensityLabel: string;
};

export type IntervalTemplate = CyclingTemplate | RunningTemplate;

export const INTERVAL_SPORT_LABELS: Record<IntervalSport, string> = {
  cycling: "Cykling",
  running: "Löpning",
};

export const GOALS_BY_SPORT: Record<IntervalSport, TrainingGoal[]> = {
  cycling: ["vo2max", "threshold", "sweet_spot", "anaerobic"],
  running: ["vo2max", "threshold", "economy"],
};

export const TRAINING_GOAL_META: Record<
  TrainingGoal,
  {
    label: string;
    short: string;
    description: string;
    zoneHintCycling: string;
    zoneHintRunning: string;
  }
> = {
  anaerobic: {
    label: "Anaerob kapacitet",
    short: "Anaerob",
    description:
      "Korta, mycket hårda intervaller för attacker, backryck och upprepade kraftinsatser.",
    zoneHintCycling: "Cirka 121–150 %+ av FTP (30 sek kan behöva ännu högre).",
    zoneHintRunning: "Nära max / sprint — styr på tempo och känsla, inte puls.",
  },
  vo2max: {
    label: "VO₂max",
    short: "VO₂max",
    description:
      "Hög intensitet som utvecklar maximal aerob kapacitet. Särskilt användbart vid ca 3–5 minuters intervaller.",
    zoneHintCycling: "Cirka 106–120 % av FTP.",
    zoneHintRunning: "Cirka 3–5 km-fart (ansträngning 9–10 mot slutet).",
  },
  threshold: {
    label: "Tröskel",
    short: "Tröskel",
    description:
      "Kontrollerat hårt — förbättrar farten du kan hålla länge och förmågan att hantera laktat.",
    zoneHintCycling: "Cirka 95–105 % av FTP.",
    zoneHintRunning: "Tröskelfart (ansträngning 7–8) — bra för 10 km till maraton.",
  },
  sweet_spot: {
    label: "Sweet spot",
    short: "Sweet spot",
    description:
      "Hårt men kontrollerat — mycket tid nära tröskeln utan samma belastning som rena tröskelpass.",
    zoneHintCycling: "Cirka 88–94 % av FTP.",
    zoneHintRunning: "",
  },
  economy: {
    label: "Löpekonomi & snabbhet",
    short: "Snabbhet",
    description:
      "Korta repetitioner som förbättrar stegfrekvens, koordination och avslappnad fart i hög hastighet.",
    zoneHintCycling: "",
    zoneHintRunning: "1500–3000 m-fart (ansträngning 9–10), korta vilor.",
  },
};

export function goalZoneHint(
  goal: TrainingGoal,
  sport: IntervalSport
): string {
  const meta = TRAINING_GOAL_META[goal];
  return sport === "running" ? meta.zoneHintRunning : meta.zoneHintCycling;
}

export const RACE_DISTANCES: {
  id: RaceDistanceId;
  label: string;
  meters: number;
  /** Rimlig defaulttid för wizard (sekunder). */
  defaultTimeSec: number;
}[] = [
  { id: "5k", label: "5 km", meters: 5000, defaultTimeSec: 25 * 60 },
  { id: "10k", label: "10 km", meters: 10000, defaultTimeSec: 52 * 60 },
  {
    id: "half",
    label: "Halvmaraton",
    meters: 21097.5,
    defaultTimeSec: 1 * 3600 + 45 * 60,
  },
  {
    id: "marathon",
    label: "Maraton",
    meters: 42195,
    defaultTimeSec: 3 * 3600 + 45 * 60,
  },
];

export const RUN_FOCUS_OPTIONS: { id: RunFocus; label: string }[] = [
  { id: "5_10k", label: "5–10 km" },
  { id: "half", label: "Halvmaraton" },
  { id: "marathon", label: "Maraton" },
  { id: "general", label: "Allmän löpkapacitet" },
];

/** Känslotabell (styrhjälp — särskilt när puls inte hinner med). */
export const RUN_FEEL_TABLE: {
  label: string;
  feel: string;
  rpe: string;
}[] = [
  { label: "Lätt distans", feel: "Kan prata obehindrat", rpe: "2–3" },
  { label: "Maratonfart", feel: "Kontrollerat, fokuserat", rpe: "5–6" },
  { label: "Tröskelfart", feel: "Hårt men kontrollerat", rpe: "7–8" },
  { label: "10 km-fart", feel: "Hårt, men jämnt", rpe: "8" },
  { label: "5 km-fart", feel: "Mycket hårt", rpe: "9" },
  {
    label: "VO₂max-intervaller",
    feel: "Nära max mot slutet",
    rpe: "9–10",
  },
  { label: "Sprint / 1500–3000 m", feel: "Maximal eller nära", rpe: "10" },
];

export const CYCLING_TEMPLATES: CyclingTemplate[] = [
  {
    id: "bike-30-30",
    sport: "cycling",
    goals: ["anaerobic", "vo2max"],
    name: "30/30 VO₂max",
    durationLabel: "30/30 sek",
    intensityPctMin: 120,
    intensityPctMax: 150,
    repsMin: 20,
    repsMax: 30,
    restLabel: "4–5 min mellan block",
    structureHint: "2–3 × 10 min med 30 s hårt / 30 s lugnt (40–60 % av FTP i vila)",
    primaryEffect: "VO₂max med hög anaerob stimulans",
    tips: [
      "Börja med 2 × 10 min. Om du tappar effekt kraftigt: sänk målet eller korta blocket.",
      "Den lugna 30-sekunden ska vara riktigt lugn — kvaliteten på de hårda avgör passet.",
    ],
  },
  {
    id: "bike-30s",
    sport: "cycling",
    goals: ["anaerobic"],
    name: "30 sek hårt",
    durationLabel: "30 sek",
    intensityPctMin: 150,
    intensityPctMax: 200,
    repsMin: 12,
    repsMax: 24,
    restLabel: "Full återhämtning mellan (eller enligt block)",
    structureHint: "2–3 × 6–10 arbetsintervall",
    primaryEffect: "Anaerob kapacitet, VO₂-stimulans",
    tips: [
      "Korta intervaller styrs sämre av enbart % FTP — håll jämn kvalitet genom hela passet.",
    ],
  },
  {
    id: "bike-1min",
    sport: "cycling",
    goals: ["anaerobic", "vo2max"],
    name: "1 min hårt",
    durationLabel: "1 min",
    intensityPctMin: 130,
    intensityPctMax: 160,
    repsMin: 12,
    repsMax: 16,
    restLabel: "1–2 min mellan intervaller, 4 min mellan block",
    structureHint: "2 × 6–8 × 1 min",
    primaryEffect: "VO₂max och anaerob kapacitet",
    tips: ["Håll dig till en effekt du klarar i sista blocket — inte bara första."],
  },
  {
    id: "bike-2min",
    sport: "cycling",
    goals: ["vo2max"],
    name: "2 min VO₂max",
    durationLabel: "2 min",
    intensityPctMin: 120,
    intensityPctMax: 140,
    repsMin: 5,
    repsMax: 6,
    restLabel: "2 min",
    primaryEffect: "VO₂max",
    tips: [
      "Sikta på jämn effekt genom alla intervaller.",
      "Cirka 15–25 min sammanlagd hård arbetstid är ett bra riktmärke för många.",
    ],
  },
  {
    id: "bike-3min",
    sport: "cycling",
    goals: ["vo2max"],
    name: "3 min VO₂max",
    durationLabel: "3 min",
    intensityPctMin: 115,
    intensityPctMax: 130,
    repsMin: 4,
    repsMax: 6,
    restLabel: "3 min",
    primaryEffect: "VO₂max",
    tips: [
      "Klassiskt format för maximal syreupptagning.",
      "Öka hellre antal repetitioner än effekt när du utvecklas.",
    ],
  },
  {
    id: "bike-4min",
    sport: "cycling",
    goals: ["vo2max"],
    name: "4 min VO₂max",
    durationLabel: "4 min",
    intensityPctMin: 108,
    intensityPctMax: 120,
    repsMin: 4,
    repsMax: 5,
    restLabel: "3 min",
    structureHint: "Klassiskt 4 × 4",
    primaryEffect: "VO₂max",
    tips: [
      "Sikta på jämn, hög effekt som du kan hålla genom alla fyra.",
      "Att köra första för hårt och tappa 10–15 % mot slutet är sällan bättre.",
    ],
  },
  {
    id: "bike-5min",
    sport: "cycling",
    goals: ["vo2max"],
    name: "5 min VO₂max",
    durationLabel: "5 min",
    intensityPctMin: 105,
    intensityPctMax: 115,
    repsMin: 4,
    repsMax: 6,
    restLabel: "3–5 min",
    primaryEffect: "VO₂max",
    tips: [
      "Lite längre arbetstid per intervall — håll dig i den lägre delen av spannet om du är osäker.",
    ],
  },
  {
    id: "bike-10min-ss",
    sport: "cycling",
    goals: ["sweet_spot"],
    name: "10 min sweet spot",
    durationLabel: "10 min",
    intensityPctMin: 88,
    intensityPctMax: 94,
    repsMin: 3,
    repsMax: 4,
    restLabel: "3–5 min",
    primaryEffect: "Sweet spot — tröskelnära volym",
    tips: [
      "Mycket arbete nära tröskeln med något lägre belastning än rena tröskelpass.",
    ],
  },
  {
    id: "bike-10min-th",
    sport: "cycling",
    goals: ["threshold"],
    name: "10 min tröskel",
    durationLabel: "10 min",
    intensityPctMin: 95,
    intensityPctMax: 105,
    repsMin: 3,
    repsMax: 4,
    restLabel: "3–5 min",
    primaryEffect: "Tröskel, hög aerob effekt",
    tips: ["Bra första tröskelpass. Öka till 15–20 min när kvaliteten sitter."],
  },
  {
    id: "bike-15min-th",
    sport: "cycling",
    goals: ["threshold", "sweet_spot"],
    name: "15 min tröskel",
    durationLabel: "15 min",
    intensityPctMin: 90,
    intensityPctMax: 100,
    repsMin: 2,
    repsMax: 3,
    restLabel: "4–5 min",
    primaryEffect: "Tröskel och uthållighet",
    tips: [
      "Håll jämn effekt — det är tröskelpasset, inte ett VO₂max-pass på 110 %.",
    ],
  },
  {
    id: "bike-20min-th",
    sport: "cycling",
    goals: ["threshold"],
    name: "20 min tröskel",
    durationLabel: "20 min",
    intensityPctMin: 90,
    intensityPctMax: 100,
    repsMin: 2,
    repsMax: 2,
    restLabel: "5–8 min",
    primaryEffect: "Tröskel, aerob uthållighet",
    tips: [
      "2 × 20 är ett klassiskt format. 110 % i 20 min är inte ett normalt tröskelpass.",
    ],
  },
];

export const RUNNING_TEMPLATES: RunningTemplate[] = [
  {
    id: "run-30s",
    sport: "running",
    goals: ["economy"],
    name: "30 sek snabbhet",
    durationLabel: "30 sek",
    paceKey: "economy",
    intensityLabel: "1500–3000 m-fart",
    repsMin: 10,
    repsMax: 15,
    restLabel: "30–45 sek jogg",
    structureHint: "15 × 30 sek",
    primaryEffect: "Löpekonomi, snabbhet",
    tips: [
      "Puls hinner inte styra — håll tempo och avslappnad teknik.",
      "Korta explosiva repetitioner med tillräcklig vila tränar snabbhet; kortare vila ger mer aerob stimulans.",
    ],
  },
  {
    id: "run-1min",
    sport: "running",
    goals: ["vo2max", "economy"],
    name: "1 min VO₂max",
    durationLabel: "1 min",
    paceKey: "three_to_five_k",
    intensityLabel: "3–5 km-fart",
    repsMin: 8,
    repsMax: 12,
    restLabel: "1 min jogg",
    structureHint: "10 × 1 min",
    primaryEffect: "VO₂max, snabbhet",
    tips: [
      "Bra när du vill få in mycket högintensivt arbete utan långa drag.",
    ],
  },
  {
    id: "run-2min",
    sport: "running",
    goals: ["vo2max"],
    name: "2 min VO₂max",
    durationLabel: "2 min",
    paceKey: "three_to_five_k",
    intensityLabel: "3–5 km-fart",
    repsMin: 5,
    repsMax: 8,
    restLabel: "2 min jogg",
    structureHint: "6 × 2 min",
    primaryEffect: "VO₂max",
    tips: ["Sikta på jämn fart genom alla intervaller."],
  },
  {
    id: "run-3min",
    sport: "running",
    goals: ["vo2max"],
    name: "3 min VO₂max",
    durationLabel: "3 min",
    paceKey: "three_to_five_k",
    intensityLabel: "3–5 km-fart",
    repsMin: 4,
    repsMax: 6,
    restLabel: "2 min jogg",
    structureHint: "5 × 3 min",
    primaryEffect: "VO₂max",
    tips: [
      "Syreupptagningen hinner stiga ordentligt — håll dig i spannet och spara till sista.",
    ],
  },
  {
    id: "run-4min",
    sport: "running",
    goals: ["vo2max"],
    name: "4 min VO₂max",
    durationLabel: "4 min",
    paceKey: "three_to_five_k",
    intensityLabel: "3–5 km-fart",
    repsMin: 4,
    repsMax: 5,
    restLabel: "2–3 min jogg",
    structureHint: "4 × 4 min",
    primaryEffect: "VO₂max",
    tips: [
      "Klassiskt format. Cirka 15–25 min sammanlagd intervalltid är en bra utgångspunkt.",
    ],
  },
  {
    id: "run-5min",
    sport: "running",
    goals: ["vo2max"],
    name: "5 min VO₂max",
    durationLabel: "5 min",
    paceKey: "five_k",
    intensityLabel: "5 km-fart",
    repsMin: 4,
    repsMax: 5,
    restLabel: "2–3 min jogg",
    structureHint: "5 × 5 min",
    primaryEffect: "VO₂max",
    tips: [
      "Lite mer kontrollerad VO₂max-fart — håll 5 km-känsla, inte sprint första minuten.",
    ],
  },
  {
    id: "run-10min-th",
    sport: "running",
    goals: ["threshold"],
    name: "10 min tröskel",
    durationLabel: "10 min",
    paceKey: "threshold",
    intensityLabel: "Tröskelfart",
    repsMin: 3,
    repsMax: 4,
    restLabel: "2 min jogg",
    structureHint: "3 × 10 min",
    primaryEffect: "Tröskel",
    tips: [
      "Hårt men kontrollerat (RPE 7–8). Bra för 10 km, halvmaraton och maraton.",
    ],
  },
  {
    id: "run-15min-th",
    sport: "running",
    goals: ["threshold"],
    name: "15 min tröskel",
    durationLabel: "15 min",
    paceKey: "threshold",
    intensityLabel: "Tröskelfart",
    repsMin: 2,
    repsMax: 3,
    restLabel: "3 min jogg",
    structureHint: "2 × 15 min",
    primaryEffect: "Tröskel",
    tips: ["Öka volymen när 3 × 10 sitter med jämn fart."],
  },
  {
    id: "run-20min-th",
    sport: "running",
    goals: ["threshold"],
    name: "20 min tröskel",
    durationLabel: "20 min",
    paceKey: "threshold_easy",
    intensityLabel: "Tröskel / strax under",
    repsMin: 2,
    repsMax: 2,
    restLabel: "3–5 min jogg",
    structureHint: "2 × 20 min",
    primaryEffect: "Tröskel och uthållighet",
    tips: [
      "Strax under ren tröskel så du håller kvaliteten i båda dragen.",
    ],
  },
];

const DEFAULT_CYCLING_BY_GOAL: Record<TrainingGoal, string> = {
  anaerobic: "bike-30-30",
  vo2max: "bike-4min",
  threshold: "bike-10min-th",
  sweet_spot: "bike-10min-ss",
  economy: "bike-4min",
};

const DEFAULT_RUNNING_BY_GOAL: Record<TrainingGoal, string> = {
  anaerobic: "run-30s",
  vo2max: "run-4min",
  threshold: "run-10min-th",
  sweet_spot: "run-10min-th",
  economy: "run-30s",
};

/** Fokus påverkar vilket defaultpass vi tippar för VO₂max/tröskel. */
export function recommendedTemplateId(
  goal: TrainingGoal,
  sport: IntervalSport,
  focus: RunFocus = "general"
): string {
  if (sport === "cycling") return DEFAULT_CYCLING_BY_GOAL[goal];
  if (goal === "vo2max" && (focus === "5_10k" || focus === "general")) {
    return "run-4min";
  }
  if (goal === "threshold" && focus === "marathon") return "run-20min-th";
  if (goal === "threshold" && focus === "half") return "run-15min-th";
  return DEFAULT_RUNNING_BY_GOAL[goal];
}

export function isTrainingGoal(v: string): v is TrainingGoal {
  return v in TRAINING_GOAL_META;
}

export function isIntervalSport(v: string): v is IntervalSport {
  return v === "cycling" || v === "running";
}

export function isExperienceLevel(v: string): v is ExperienceLevel {
  return v === "beginner" || v === "intermediate" || v === "advanced";
}

export function isRaceDistanceId(v: string): v is RaceDistanceId {
  return RACE_DISTANCES.some((d) => d.id === v);
}

export function isRunFocus(v: string): v is RunFocus {
  return RUN_FOCUS_OPTIONS.some((o) => o.id === v);
}

export function allTemplates(): IntervalTemplate[] {
  return [...CYCLING_TEMPLATES, ...RUNNING_TEMPLATES];
}

export function templatesForGoal(
  goal: TrainingGoal,
  sport: IntervalSport
): IntervalTemplate[] {
  return allTemplates().filter(
    (t) => t.sport === sport && t.goals.includes(goal)
  );
}

export function getTemplateById(id: string): IntervalTemplate | undefined {
  return allTemplates().find((t) => t.id === id);
}

export function suggestedReps(
  template: IntervalTemplate,
  experience: ExperienceLevel
): { reps: number; label: string } {
  const { repsMin, repsMax } = template;
  if (experience === "beginner") {
    return { reps: repsMin, label: `${repsMin}` };
  }
  if (experience === "advanced") {
    return { reps: repsMax, label: `${repsMax}` };
  }
  return {
    reps: Math.round((repsMin + repsMax) / 2),
    label: repsMin === repsMax ? `${repsMin}` : `${repsMin}–${repsMax}`,
  };
}

export function wattsFromFtp(ftp: number, pct: number): number {
  if (!Number.isFinite(ftp) || ftp <= 0) return 0;
  return Math.round((ftp * pct) / 100);
}

export function wattRangeLabel(
  ftp: number,
  pctMin: number,
  pctMax: number
): string {
  const lo = wattsFromFtp(ftp, pctMin);
  const hi = wattsFromFtp(ftp, pctMax);
  if (!lo || !hi) return "—";
  return lo === hi ? `${lo} W` : `${lo}–${hi} W`;
}

/** Riegel: T2 = T1 × (D2/D1)^1.06 */
export function riegelPredictSec(
  timeSec: number,
  fromMeters: number,
  toMeters: number,
  exponent = 1.06
): number {
  if (timeSec <= 0 || fromMeters <= 0 || toMeters <= 0) return 0;
  return timeSec * Math.pow(toMeters / fromMeters, exponent);
}

export function paceSecPerKm(timeSec: number, distanceMeters: number): number {
  if (timeSec <= 0 || distanceMeters <= 0) return 0;
  return timeSec / (distanceMeters / 1000);
}

export function formatPaceMinPerKm(secPerKm: number): string {
  if (!Number.isFinite(secPerKm) || secPerKm <= 0) return "—";
  const total = Math.round(secPerKm);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function formatPaceRange(minSec: number, maxSec: number): string {
  const a = formatPaceMinPerKm(Math.min(minSec, maxSec));
  const b = formatPaceMinPerKm(Math.max(minSec, maxSec));
  return a === b ? `${a} /km` : `${a}–${b} /km`;
}

export function hmsToSec(h: number, m: number, s: number): number {
  return (
    Math.max(0, h) * 3600 +
    Math.max(0, Math.min(59, m)) * 60 +
    Math.max(0, Math.min(59, s))
  );
}

export function secToHms(total: number): { h: number; m: number; s: number } {
  const t = Math.max(0, Math.floor(total));
  return {
    h: Math.floor(t / 3600),
    m: Math.floor((t % 3600) / 60),
    s: t % 60,
  };
}

export type PersonalRunPaces = {
  /** 1500–3000 m-fart (snabbare → långsammare) */
  economyMin: number;
  economyMax: number;
  threeToFiveMin: number;
  threeToFiveMax: number;
  fiveK: number;
  tenK: number;
  thresholdMin: number;
  thresholdMax: number;
  thresholdEasy: number;
  marathon: number;
  easy: number;
  /** Beräknade ekvivalenta tävlingstider (sek) */
  equiv: Record<RaceDistanceId, number>;
};

export function buildPersonalRunPaces(
  raceId: RaceDistanceId,
  timeSec: number
): PersonalRunPaces | null {
  const race = RACE_DISTANCES.find((d) => d.id === raceId);
  if (!race || timeSec < 60) return null;

  const equivSec = (meters: number) =>
    riegelPredictSec(timeSec, race.meters, meters);

  const equiv: Record<RaceDistanceId, number> = {
    "5k": equivSec(5000),
    "10k": equivSec(10000),
    half: equivSec(21097.5),
    marathon: equivSec(42195),
  };

  const p15 = paceSecPerKm(equivSec(1500), 1500);
  const p3 = paceSecPerKm(equivSec(3000), 3000);
  const p5 = paceSecPerKm(equiv["5k"], 5000);
  const p10 = paceSecPerKm(equiv["10k"], 10000);
  const pHalf = paceSecPerKm(equiv.half, 21097.5);
  const pMar = paceSecPerKm(equiv.marathon, 42195);

  /** Tröskel ≈ mellan 10 km och halv — kontrollerat hårt. */
  const thresholdCenter = p10 * 0.45 + pHalf * 0.55;

  return {
    economyMin: Math.min(p15, p3),
    economyMax: Math.max(p15, p3),
    threeToFiveMin: Math.min(p3, p5),
    threeToFiveMax: Math.max(p3, p5),
    fiveK: p5,
    tenK: p10,
    thresholdMin: thresholdCenter - 5,
    thresholdMax: thresholdCenter + 5,
    thresholdEasy: thresholdCenter + 10,
    marathon: pMar,
    easy: pMar * 1.12,
    equiv,
  };
}

export function paceRangeForKey(
  paces: PersonalRunPaces,
  key: RunningPaceKey
): { min: number; max: number; label: string } {
  switch (key) {
    case "economy":
      return {
        min: paces.economyMin,
        max: paces.economyMax,
        label: "1500–3000 m-fart",
      };
    case "three_to_five_k":
      return {
        min: paces.threeToFiveMin,
        max: paces.threeToFiveMax,
        label: "3–5 km-fart",
      };
    case "five_k":
      return {
        min: paces.fiveK - 2,
        max: paces.fiveK + 2,
        label: "5 km-fart",
      };
    case "threshold":
      return {
        min: paces.thresholdMin,
        max: paces.thresholdMax,
        label: "Tröskelfart",
      };
    case "threshold_easy":
      return {
        min: paces.thresholdEasy - 3,
        max: paces.thresholdEasy + 5,
        label: "Tröskel / strax under",
      };
  }
}

export type SessionSummary = {
  title: string;
  structure: string;
  intensity: string;
  target: string;
  targetLabel: string;
  rest: string;
  effect: string;
};

export function formatCyclingSessionSummary(
  template: CyclingTemplate,
  experience: ExperienceLevel,
  ftp: number
): SessionSummary {
  const reps = suggestedReps(template, experience);
  const structure =
    template.structureHint || `${reps.label} × ${template.durationLabel}`;
  return {
    title: template.name,
    structure,
    intensity: `${template.intensityPctMin}–${template.intensityPctMax} % av FTP`,
    target: wattRangeLabel(
      ftp,
      template.intensityPctMin,
      template.intensityPctMax
    ),
    targetLabel: "Watt",
    rest: template.restLabel,
    effect: template.primaryEffect,
  };
}

export function formatRunningSessionSummary(
  template: RunningTemplate,
  experience: ExperienceLevel,
  paces: PersonalRunPaces
): SessionSummary {
  const reps = suggestedReps(template, experience);
  const structure =
    template.structureHint || `${reps.label} × ${template.durationLabel}`;
  const range = paceRangeForKey(paces, template.paceKey);
  return {
    title: template.name,
    structure,
    intensity: template.intensityLabel,
    target: formatPaceRange(range.min, range.max),
    targetLabel: "Tempo",
    rest: template.restLabel,
    effect: template.primaryEffect,
  };
}

export const CYCLING_GENERAL_TIPS = [
  "Börja med 1–2 hårda cykelpass i veckan. Fyll på med lugn distans däremellan.",
  "Öka inte både effekt och antal intervaller samtidigt — lägg hellre till en repetition.",
  "Använd rätt effekt för rätt syfte. 20 min på 110 % av FTP är inte ett normalt tröskelpass.",
  "Uppdatera FTP när formen förändras — alla wattmål bygger på en någorlunda korrekt FTP.",
] as const;

export const RUNNING_GENERAL_TIPS = [
  "Börja med 1–2 kvalitativa löppass i veckan. Fyll på med lätt distans däremellan.",
  "För korta intervaller (≤1 min) styr du främst på tempo och känsla — pulsen hinner inte med.",
  "Öka inte både fart och antal repetitioner samtidigt. Lägg hellre till en repetition.",
  "Uppdatera ditt referenslopp när formen förändras — farttabellen bygger på aktuell kapacitet.",
] as const;

/** Bakåtkompatibilitet */
export const GENERAL_TIPS = CYCLING_GENERAL_TIPS;

export function formatSessionSummary(
  template: IntervalTemplate,
  experience: ExperienceLevel,
  ftp: number
): SessionSummary {
  if (template.sport === "cycling") {
    return formatCyclingSessionSummary(template, experience, ftp);
  }
  // Fallback utan paces — ska inte användas för löpning i UI
  const reps = suggestedReps(template, experience);
  return {
    title: template.name,
    structure:
      template.structureHint || `${reps.label} × ${template.durationLabel}`,
    intensity: template.intensityLabel,
    target: "—",
    targetLabel: "Tempo",
    rest: template.restLabel,
    effect: template.primaryEffect,
  };
}
